const CM_PER_INCH = 2.54;
const UNIT_KEY = 'size-chart-unit';
// More charts than this are offered in a selection box instead of a row of buttons.
const MAX_TABS = 3;

class SizeChartDialog extends HTMLElement {
  #controller = new AbortController();
  #opener = null;
  #previousOverflow = '';
  #lockOwned = false;

  connectedCallback() {
    this.#controller = new AbortController();
    const { signal } = this.#controller;
    const dialog = this.querySelector('dialog');
    const owner = this.closest('variant-picker') || this.parentElement;
    if (!dialog || !owner) return;

    owner.addEventListener('click', (event) => {
      const button = event.target.closest?.(`[data-open-sizechart="${CSS.escape(this.id)}"]`);
      if (!button) return;
      event.preventDefault();
      if (dialog.open) return;
      this.#opener = button;
      this.#previousOverflow = document.documentElement.style.overflow;
      dialog.showModal();
      this.#setView(dialog, 'chart');
      this.#updateContent(button.closest('variant-picker') || owner, dialog);
      document.documentElement.style.overflow = 'hidden';
      this.#lockOwned = true;
    }, { signal });

    dialog.addEventListener('click', (event) => {
      if (event.target.closest?.('[data-close-size-chart]')) this.#close(dialog);
      const tab = event.target.closest?.('[data-chart]');
      if (tab) this.#show(dialog, tab.dataset.chart);
      const view = event.target.closest?.('[data-view]');
      if (view && view !== dialog) this.#setView(dialog, view.dataset.view);
      const unit = event.target.closest?.('[data-unit]');
      if (unit) this.#setUnit(dialog, unit.dataset.unit, true);
      // A row the customer taps stands out, so its numbers are easy to read across.
      const row = event.target.closest?.('.size-chart__table tbody tr');
      if (row) row.parentElement.querySelectorAll('tr').forEach((other) => other.classList.toggle('is-selected', other === row));
    }, { signal });
    dialog.addEventListener('cancel', (event) => { event.preventDefault(); this.#close(dialog); }, { signal });
    dialog.addEventListener('close', () => { this.#releaseLock(); this.#restoreFocus(); }, { signal });
    dialog.addEventListener('change', (event) => {
      if (event.target.matches?.('.size-chart-drawer__select')) this.#show(dialog, event.target.value);
    }, { signal });
    owner.addEventListener('change', (event) => {
      if (!dialog.contains(event.target)) this.#updateContent(owner, dialog);
    }, { signal });
  }

  disconnectedCallback() {
    this.#controller.abort();
    const dialog = this.querySelector('dialog');
    if (dialog?.open) dialog.close();
    this.#releaseLock();
    this.#restoreFocus();
  }

  #close(dialog) {
    if (dialog.open) dialog.close();
    this.#releaseLock();
    this.#restoreFocus();
  }

  #releaseLock() {
    if (!this.#lockOwned) return;
    document.documentElement.style.overflow = this.#previousOverflow;
    this.#lockOwned = false;
  }

  #restoreFocus() {
    const opener = this.#opener;
    this.#opener = null;
    opener?.isConnected && opener.focus();
  }

  // The value of an option the customer has chosen in the picker, in lower case.
  #chosen(owner, name) {
    let value = '';
    for (const option of owner.querySelectorAll('select option:checked, fieldset input:checked')) {
      if (option.dataset.optionName?.toLowerCase() === name) value = option.value.toLowerCase();
    }
    return value;
  }

  // The chart of the variant the customer has chosen: by the model code in its SKU, else by its style name,
  // else the one the product is tagged with.
  #chartFor(owner, dialog) {
    let sku = '';
    try {
      sku = JSON.parse(owner.querySelector('script[type="application/json"]:not([data-variant-catalog])')?.textContent || '{}')?.sku || '';
    } catch { /* no selected variant to read */ }
    const model = sku.split('-')[2];
    const own = model && [...dialog.querySelectorAll('[data-sku-model]')].find((content) => content.dataset.skuModel === model);
    if (own) return own.dataset.styleType;
    return this.#chosen(owner, 'style').includes('hoodie') ? 'hoodie' : this.dataset.defaultChart || 't-shirt';
  }

  #updateContent(owner, dialog) {
    const type = this.#chartFor(owner, dialog);
    // A product sold in several styles lets the customer look at each style's chart.
    const charts = new Set((this.dataset.productCharts || '').split(',').filter(Boolean)).add(type);
    const tabs = dialog.querySelector('.size-chart-drawer__tabs');
    if (tabs) {
      const choices = [...dialog.querySelectorAll('[data-style-type]')].filter((content) => charts.has(content.dataset.styleType));
      if (choices.length > MAX_TABS) {
        // Too many charts for a row of buttons: one box to pick from.
        const select = document.createElement('select');
        select.className = 'size-chart-drawer__select';
        select.setAttribute('aria-label', 'Style');
        select.replaceChildren(...choices.map((content) => new Option(content.dataset.chartTab, content.dataset.styleType)));
        tabs.replaceChildren(select);
      } else {
        tabs.replaceChildren(...choices.map((content) => {
          const tab = document.createElement('button');
          tab.type = 'button';
          tab.className = 'size-chart-drawer__tab';
          tab.dataset.chart = content.dataset.styleType;
          tab.textContent = content.dataset.chartTab;
          return tab;
        }));
      }
      tabs.hidden = choices.length < 2;
    }
    // The row of the size the customer has chosen stands out in every chart that has it.
    const size = this.#chosen(owner, 'size');
    dialog.querySelectorAll('tbody tr').forEach((row) => {
      row.classList.toggle('is-selected', Boolean(size) && row.querySelector('th')?.textContent.trim().toLowerCase() === size);
    });
    let unit = 'in';
    try { unit = localStorage.getItem(UNIT_KEY) || unit; } catch { /* storage is blocked */ }
    this.#setUnit(dialog, unit, false);
    this.#show(dialog, type);
  }

  #show(dialog, type) {
    dialog.querySelectorAll('[data-style-type]').forEach((content) => { content.hidden = content.dataset.styleType !== type; });
    dialog.querySelectorAll('[data-chart]').forEach((tab) => {
      tab.setAttribute('aria-pressed', String(tab.dataset.chart === type));
      if (tab.dataset.chart !== type) return;
      // Bring the chosen tab into its row without scrolling anything else.
      const row = tab.parentElement;
      row.scrollLeft += tab.getBoundingClientRect().left - row.getBoundingClientRect().left - (row.clientWidth - tab.offsetWidth) / 2;
    });
    const select = dialog.querySelector('.size-chart-drawer__select');
    if (select) select.value = type;
    const title = dialog.querySelector('.size-chart-drawer__title');
    const shown = dialog.querySelector(`[data-style-type="${type}"]`);
    if (title && shown) title.textContent = shown.dataset.chartTitle;
  }

  // The drawer shows one view at a time: the chart, or how to measure.
  #setView(dialog, view) {
    dialog.dataset.view = view;
    dialog.querySelectorAll('button[data-view]').forEach((button) => button.setAttribute('aria-pressed', String(button.dataset.view === view)));
    dialog.querySelector('.size-chart-drawer__body')?.scrollTo(0, 0);
  }

  // Each cell holds its measurement in the unit the maker gives; the other unit is worked out from it.
  #setUnit(dialog, unit, remember) {
    if (unit !== 'cm') unit = 'in';
    dialog.querySelectorAll('td[data-in], td[data-cm]').forEach((cell) => {
      const given = cell.dataset[unit];
      const value = given !== undefined ? Number(given)
        : unit === 'cm' ? Number(cell.dataset.in) * CM_PER_INCH : Number(cell.dataset.cm) / CM_PER_INCH;
      cell.textContent = String(Math.round(value * 10) / 10);
    });
    dialog.querySelectorAll('[data-unit]').forEach((button) => button.setAttribute('aria-pressed', String(button.dataset.unit === unit)));
    dialog.querySelectorAll('.size-chart__unit').forEach((label) => { label.textContent = unit; });
    if (!remember) return;
    try { localStorage.setItem(UNIT_KEY, unit); } catch { /* storage is blocked */ }
  }
}

if (!customElements.get('size-chart-dialog')) customElements.define('size-chart-dialog', SizeChartDialog);
